import { Injectable } from '@angular/core';
import {Observable} from "rxjs";
import {environment} from "../../../../environments/environment";
import type { ChatResponse, QueryResult, Source } from '../colimind-types';

export interface ColimindChatRequest {
  input: string;
  metadata: Record<string, string>;
}

export interface ColimindChatStreamEvent {
  type: 'created' | 'delta' | 'completed' | 'error' | string;
  text?: string | null;
  responseId?: string | null;
  threadId?: string | null;
  raw?: unknown;
  metaData?: unknown;
  // Present on the `completed` event.
  session_id?: string | null;
  conversation_id?: string | null;
  answer_text?: string | null;
  sql_query?: string | null;
  sources?: Source[] | null;
  query_result?: QueryResult | null;
}

// The backend sends nothing more after these events
const FINAL_EVENT_TYPES = ['completed', 'response.failed', 'error', 'response.approval_required'];

/** Maps a `completed` stream event onto the structured response the cards render from. */
export function toChatResponse(
  event: ColimindChatStreamEvent,
  fallbackText: string,
  fallbackSessionId?: string,
): ChatResponse {
  return {
    session_id: event.session_id ?? fallbackSessionId ?? '',
    conversation_id: event.conversation_id ?? '',
    answer_text: event.answer_text ?? fallbackText,
    sql_query: event.sql_query ?? undefined,
    sources: event.sources ?? [],
    query_result: event.query_result ?? undefined,
  };
}

@Injectable({
  providedIn: 'root'
})
export class ColimindApiService {

  private baseUrl = environment.colimind.baseUrl;
  private apiUrl = `${this.baseUrl}/${environment.colimind.apiVersionPath}`;

  stream(request: ColimindChatRequest): Observable<ColimindChatStreamEvent> {
    return new Observable<ColimindChatStreamEvent>((observer) => {
      const abortController = new AbortController();

      fetch(`${this.apiUrl}${environment.colimind.chatStreamPath}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('JWT') ?? '',
        },
        body: JSON.stringify(request),
        signal: abortController.signal,
      })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(`MAUI chat stream failed with HTTP ${response.status}`);
          }

          if (!response.body) {
            throw new Error('Browser does not support readable response streams.');
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder('utf-8');
          let buffer = '';

          while (true) {
            const { value, done } = await reader.read();

            if (done) {
              observer.complete();
              return;
            }

            buffer += decoder.decode(value, { stream: true });

            const blocks = buffer.split(/\r?\n\r?\n/);
            buffer = blocks.pop() ?? '';

            for (const block of blocks) {
              const event = this.parseSseBlock(block);

              if (!event) {
                continue;
              }

              observer.next(event);

              if (FINAL_EVENT_TYPES.includes(event.type)) {
                await reader.cancel();
                observer.complete();
                return;
              }
            }
          }
        })
        .catch((error) => {
          if (!abortController.signal.aborted) {
            observer.error(error);
          }
        });

      return () => abortController.abort();
    });
  }

  private parseSseBlock(block: string): ColimindChatStreamEvent | null {
    const lines = block.split(/\r?\n/);

    let eventType = 'message';
    const dataLines: string[] = [];

    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventType = line.substring('event:'.length).trim();
      }

      if (line.startsWith('data:')) {
        dataLines.push(line.substring('data:'.length).trim());
      }
    }

    if (dataLines.length === 0) {
      return null;
    }

    const data = dataLines.join('\n');

    try {
      const parsed = JSON.parse(data) as ColimindChatStreamEvent;

      return {
        ...parsed,
        type: parsed.type ?? eventType,
      };
    } catch {
      return {
        type: eventType,
        text: data,
      };
    }
  }
}
